import { ReportSummary } from "src/app/models/models";
import {
	isRemoteSignoffFormType,
	REMOTE_SIGNOFF_V3,
} from "../ratings/container-ratings-form/shared-v2/utils/rating-form-version.utils";

type FlatReportSummary = ReportSummary & {
	maneuverChartData?: { label: string; value: number }[];
	radarChartLabels?: string[];
	radarChartDatasets?: unknown[];
};

function hasV2BundlePreviewData(reportSummary: FlatReportSummary): boolean {
	if (reportSummary.maneuverBreakdown && Object.keys(reportSummary.maneuverBreakdown).length > 0) {
		return true;
	}
	const chart = reportSummary.chartData;
	if (chart?.manueverChartData?.length || chart?.radarChartData?.labels?.length) {
		return true;
	}
	return !!(reportSummary.maneuverChartData?.length || reportSummary.radarChartLabels?.length);
}

/**
 * True for non-TS1 remote sign-off checklist payloads (eTS-3, cabin crew, cabin crew instructor)
 * with formVersion 3 and report-summary preview data.
 */
export function isV2BundleRemoteSignoffReportSummary(reportSummary?: ReportSummary | null): boolean {
	if (!reportSummary?.formType) return false;
	if (!isRemoteSignoffFormType(reportSummary.formType)) return false;
	if (Number(reportSummary.formVersion) !== REMOTE_SIGNOFF_V3) return false;
	return hasV2BundlePreviewData(reportSummary as FlatReportSummary);
}

type PdfRecommendation = ReportSummary["recommendation"] & {
	show?: boolean;
	isCheck?: boolean;
	sessionCompleted?: string;
	objectivesMet?: string;
	clearedForCheck?: string | null;
};

function parseRecommendationYesNo(value?: string | null): boolean | null {
	if (value == null || value === "-") return null;
	if (value.toUpperCase() === "YES") return true;
	if (value.toUpperCase() === "NO") return false;
	return null;
}

function parseRecommendationClearance(value?: string | null): boolean | string | null {
	if (value == null || value === "-") return null;
	if (value.toUpperCase() === "NOT APPLICABLE") return "N/A";
	if (value.toUpperCase() === "YES") return true;
	if (value.toUpperCase() === "NO") return false;
	return null;
}

function normalizeChecklistRecommendation(
	recommendation?: ReportSummary["recommendation"],
): ReportSummary["recommendation"] | undefined {
	if (!recommendation) return undefined;

	const pdf = recommendation as PdfRecommendation;
	if (pdf.show === false) return undefined;
	if (pdf.is_complete != null || pdf.is_obj_met != null || pdf.is_clear != null) {
		return recommendation;
	}
	if (pdf.sessionCompleted == null && pdf.objectivesMet == null) {
		return recommendation;
	}

	const sessionTypeCode = pdf.isCheck ? "check" : "training";
	const isComplete = parseRecommendationYesNo(pdf.sessionCompleted);
	const isObjMet = parseRecommendationYesNo(pdf.objectivesMet);
	const isClear = parseRecommendationClearance(pdf.clearedForCheck);
	return {
		sessionType: sessionTypeCode,
		sessionTypeDescription: pdf.sessionType ?? (pdf.isCheck ? "Check" : "Training"),
		...(isComplete != null ? { is_complete: isComplete } : {}),
		...(isObjMet != null ? { is_obj_met: isObjMet } : {}),
		...(isClear != null ? { is_clear: isClear } : {}),
	};
}

/** Normalize checklist API summary into the shape expected by ReportSummaryComponent. */
export function normalizeChecklistReportSummary(reportSummary: ReportSummary): ReportSummary {
	const flat = reportSummary as FlatReportSummary;
	const chart = reportSummary.chartData;

	return {
		...reportSummary,
		recommendation: normalizeChecklistRecommendation(reportSummary.recommendation),
		chartData:
			chart ??
			({
				manueverChartData: flat.maneuverChartData ?? [],
				radarChartData: {
					labels: flat.radarChartLabels ?? [],
					datasets: flat.radarChartDatasets ?? [],
				},
			} as ReportSummary["chartData"]),
	};
}
